import { AliasForm } from "@/components/dashboard/integrations/ebay/alias-form";
import { BusinessPoliciesCard } from "@/components/dashboard/integrations/ebay/business-policies-card";
import { OrderSyncCard } from "@/components/dashboard/integrations/ebay/order-sync-card";
import { ProductFetchForm } from "@/components/dashboard/integrations/ebay/product-fetch-form";
import { RemoveConfirmation } from "@/components/dashboard/integrations/ebay/remove-confirmation";
import { SyncConfigCard } from "@/components/dashboard/integrations/sync-config-card";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import GatedOverlay from "@/components/ui/gated-overlay";
import {
  accountQueryOpts,
  ebayOrderSyncConfigQueryOpts,
  ebayPoliciesQueryOpts,
} from "@/hooks/ebay";
import { isGated } from "@/hooks/gated";
import { shopifyAccountsQueryOpts } from "@/hooks/shopify";
import { syncConfigQueryOpts } from "@/hooks/sync-config";
import { getOrgIdWithFallback } from "@/lib/utils";
import { useAuth } from "@/providers/auth";
import { useOrg } from "@/providers/organization";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/$org/integrations/ebay/$alias/account")({
  component: RouteComponent,
  loader: async ({ context, params }) => {
    const { getToken, setToken, handleTokenInvalid } = context.auth;
    const orgId = getOrgIdWithFallback(context.org.orgId);

    const gated = await isGated(
      {
        token: getToken() as string,
        setToken,
        orgId: orgId,
      },
      handleTokenInvalid,
    );

    const account = await context.queryClient.ensureQueryData(
      accountQueryOpts(params.alias, {
        setToken,
        token: getToken() as string,
        orgId: orgId,
      }),
    );

    await context.queryClient.ensureQueryData(
      syncConfigQueryOpts(account.platform_connection_id, {
        setToken,
        token: getToken() as string,
        orgId: orgId,
      }),
    );

    await context.queryClient.ensureQueryData(
      ebayOrderSyncConfigQueryOpts(account.platform_connection_id, {
        setToken,
        token: getToken() as string,
        orgId: orgId,
      }),
    );

    await context.queryClient
      .ensureQueryData(
        ebayPoliciesQueryOpts(params.alias, {
          setToken,
          token: getToken() as string,
          orgId: orgId,
        }),
      )
      .catch(() => undefined);

    await context.queryClient.ensureQueryData(
      shopifyAccountsQueryOpts({
        setToken,
        token: getToken() as string,
        orgId: orgId,
      }),
    );

    return { gated: gated?.is_gated };
  },
});

function RouteComponent() {
  const { t, i18n } = useTranslation();
  const { gated } = Route.useLoaderData();
  const { org } = Route.useParams();
  const { orgId } = useOrg();
  const resolvedOrgId = getOrgIdWithFallback(orgId);

  const { alias } = Route.useParams();

  const { getToken, setToken } = useAuth();
  const { data: account } = useQuery(
    accountQueryOpts(alias, {
      setToken,
      token: getToken() as string,
      orgId: resolvedOrgId,
    }),
  );
  const { data: shopifyAccounts = [] } = useQuery(
    shopifyAccountsQueryOpts({
      setToken,
      token: getToken() as string,
      orgId: resolvedOrgId,
    }),
  );

  if (gated) {
    return <GatedOverlay org={org} />;
  }

  return (
    <div>
      <div className="mx-auto max-w-3xl">
        {account && (
          <div className="space-y-2">
            <Card>
              <CardHeader>
                <CardTitle>{t("integrations.account.details")}</CardTitle>
              </CardHeader>

              <CardContent className="space-y-4">
                <AliasForm account={account} />

                {account.refresh_token_expiry && (
                  <div className="text-sm font-semibold">
                    {t("integrations.account.expiresOn", {
                      date: new Date(
                        account.refresh_token_expiry,
                      ).toLocaleString(i18n.language, {
                        dateStyle: "full",
                        timeStyle: "short",
                        timeZone: "UTC",
                      }),
                    })}
                  </div>
                )}
              </CardContent>
              <CardFooter className="flex justify-end">
                <Button
                  form="alias-form"
                  name="intent"
                  value={"details"}
                  type="submit"
                >
                  {t("common.save")}
                </Button>
              </CardFooter>
            </Card>
            <BusinessPoliciesCard account={account} />
            <Card>
              <CardHeader>
                <CardTitle>{t("integrations.account.fetchTitle")}</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col text-sm font-semibold">
                <span>{t("integrations.account.fetchDescription")}</span>
                <span className="text-xs text-gray-600">
                  {t("integrations.account.fetchHint")}
                </span>
              </CardContent>
              <CardFooter className="flex justify-end">
                <ProductFetchForm account={account} />
              </CardFooter>
            </Card>
            <SyncConfigCard
              platformConnectionId={account.platform_connection_id}
              showEndListingWhenQuantityZero
              showExternalOrderImport
              showAutomatedExports
              exportTargetAccounts={shopifyAccounts}
              exportTargetPlatform="Shopify"
            />
            <OrderSyncCard
              ebayPlatformConnectionId={account.platform_connection_id}
            />
            <div className="flex justify-end">
              <RemoveConfirmation
                alias={account.alias}
                ebayAccId={account.id as string}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
